/**
 * HUD Styles
 * Separated from HUD.tsx for better maintainability
 */

import { HUD_Z_INDEX } from '../../../config/ui/hudDefaults.config';
import {
  DEBUG_TOOLBOX_BOTTOM_INSET,
  DEBUG_TOOLBOX_PANEL_MAX_HEIGHT,
  DEBUG_TOOLBOX_RIGHT_INSET,
} from '../framework/WindowLauncher';
import { COLORS, RAW, FONTS, ACCENT } from '../theme';

// =============================================================================
// Container Styles
// =============================================================================

export const hudContainerStyle = `
  .hud-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 100;
  }
`;

// =============================================================================
// Debug Toolbox Container
// =============================================================================

/**
 * Debug Toolbox container — bottom-right control cluster, directly ABOVE the
 * 🛠 dev launcher button (U1, 2026-09-28; DJ: "Move top left icon tool to
 * bottom right next to our other[s]"). The collapsed 🛠️ chip is
 * DebugToolbox.tsx's collapsed branch; the expanded panel grows UPWARD and is
 * capped by DEBUG_TOOLBOX_PANEL_MAX_HEIGHT + overflowY so it always stays
 * inside the viewport. The anchor is DERIVED from the N80 cluster exports in
 * ../framework/WindowLauncher — same right column as the 🛠 button, one
 * documented gap above it — never hand-placed pixels, so it cannot drift into
 * the ≡ zone or the freeze/QA chips. See DebugToolbox.placement.test.tsx.
 */
export const debugToolboxContainerStyle: React.CSSProperties = {
  position: 'absolute',
  right: DEBUG_TOOLBOX_RIGHT_INSET,
  bottom: DEBUG_TOOLBOX_BOTTOM_INSET,
  display: 'flex',
  gap: '5px',
  zIndex: HUD_Z_INDEX.debug,
  pointerEvents: 'auto',
  alignItems: 'flex-start',
  maxHeight: DEBUG_TOOLBOX_PANEL_MAX_HEIGHT,
  overflowY: 'auto',
  overflowX: 'hidden',
};

// =============================================================================
// Map Banner Styles
// =============================================================================

export const mapBannerStyle: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  left: '50%',
  transform: 'translateX(-50%)',
  maxWidth: 'calc(100vw - 32px)',
  boxSizing: 'border-box',
  textAlign: 'center',
  background: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(10px)',
  // Brass fitting border + Cinzel map name (HUD-restyle §2/§3) — was a flat
  // white 20% border + bold Inter. Gold text + glow keep the zone-banner
  // identity.
  border: `1px solid ${ACCENT.brass}`,
  borderRadius: '20px',
  padding: '8px 25px',
  color: COLORS.textAccent,
  fontFamily: FONTS.display,
  fontSize: '18px',
  fontWeight: 'bold',
  letterSpacing: '1px',
  textShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
  zIndex: HUD_Z_INDEX.panels,
  pointerEvents: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px'
};

export const mapBannerIconStyle: React.CSSProperties = {
  fontSize: '14px',
  opacity: 0.7,
  color: COLORS.textPrimary
};

export const mapIdStyle: React.CSSProperties = {
  fontSize: '10px',
  color: 'rgba(255,255,255,0.5)',
  fontFamily: 'monospace'
};

// =============================================================================
// Connection Status Styles
// =============================================================================

export const connectionStatusStyle: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: 'rgba(0, 0, 0, 0.8)',
  padding: '20px 40px',
  borderRadius: '8px',
  border: `2px solid ${RAW.red}`,
  textAlign: 'center',
  zIndex: HUD_Z_INDEX.connectionStatus,
  pointerEvents: 'auto',
  backdropFilter: 'blur(5px)'
};

export const connectionStatusTitleStyle: React.CSSProperties = {
  color: RAW.red,
  margin: '0 0 10px 0',
  fontSize: '24px'
};

export const connectionStatusMessageStyle: React.CSSProperties = {
  color: RAW.silver,
  fontSize: '14px'
};

// =============================================================================
// Action Bar Container
// =============================================================================

export const actionBarContainerStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: HUD_Z_INDEX.actionbar,
  pointerEvents: 'auto'
};

/**
 * Compact landscape action-bar band (W2a, 2026-09-28): the same absolute dock,
 * scaled about its bottom-centre so the full ten-slot row fits a 640 px
 * viewport (652 → 489 px at scale 0.75) while the natural slot geometry
 * (58×58 — gameplay-critical) stays untouched. The chat peek's bottom offset
 * is derived from `round(ACTION_BAR_NATURAL_HEIGHT × scale)`; see
 * hudCompactLayout.ts.
 */
export const actionBarCompactContainerStyle = (scale: number, bottomInset: number): React.CSSProperties => ({
  ...actionBarContainerStyle,
  bottom: `${bottomInset}px`,
  transform: `translateX(-50%) scale(${scale})`,
  transformOrigin: 'bottom center',
});

/**
 * Chat dock position (W2a). Base: bottom-left (unchanged WoW-style dock).
 * Compact: right-anchored above the scaled action band, so the peek clears the
 * left HUD column (player info + target frame) at short landscape heights.
 */
export function chatDockStyle(inset: { right: number; bottom: number } | null): React.CSSProperties {
  return {
    position: 'absolute',
    ...(inset
      ? { right: inset.right, bottom: inset.bottom }
      : { left: 16, bottom: 16 }),
    pointerEvents: 'auto',
    zIndex: HUD_Z_INDEX.panels,
  };
}

// =============================================================================
// Collapsed HUD Styles
// =============================================================================

export const collapsedHUDStyle: React.CSSProperties = {
  position: 'absolute',
  top: '50px',
  left: '10px',
  background: 'rgba(0, 0, 0, 0.5)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '4px',
  padding: '5px 10px',
  color: RAW.gray,
  fontSize: '12px',
  pointerEvents: 'auto'
};

// =============================================================================
// Micro Menu Styles
// =============================================================================

export const quickMenuContainerStyle = (y: number, offset: number): React.CSSProperties => ({
  position: 'absolute',
  bottom: `${y}px`,
  right: `${offset}px`,
  pointerEvents: 'auto'
});

// =============================================================================
// Global CSS Classes
// =============================================================================

export const hudGlobalStyles = `
  .hud-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 100;
  }

  /* PlayerInfoPanel: width + responsive widening live here; the panel surface,
     header, and HP/MP/XP rows are now self-contained theme-token inline styles
     in PlayerInfoPanel.tsx / VitalsPanel.tsx (2026-06-10 theme modernization). */
  .player-info-panel {
    width: 220px;
    padding: 8px 10px;
  }

  /* Low-HP frame state (ink-&-brass restyle §6): the border warms toward danger
     (set inline on the panel) and the brass fitting exhales a slow 2s danger
     glow. Replaces the retired "⚠️ Low Health!" text pill. The rgba glow is the
     COLORS.danger hue (#ff4757 → 255,71,87); animated box-shadow overrides the
     panel's inline base shadow only while the low-HP state is active. Honours
     reduced-motion: the warm border stays, the pulse stops. */
  @keyframes helix-lowhp-pulse {
    0%, 100% { box-shadow: 0 2px 10px rgba(0,0,0,0.45), 0 0 0 rgba(255,71,87,0), inset 0 1px 0 rgba(255,255,255,0.05); }
    50%      { box-shadow: 0 2px 10px rgba(0,0,0,0.45), 0 0 14px rgba(255,71,87,0.38), inset 0 1px 0 rgba(255,255,255,0.05); }
  }
  @media (prefers-reduced-motion: reduce) {
    .player-info-panel[data-low-health="true"] { animation: none !important; }
  }

  .quick-stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .stat-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .stat-icon {
    font-size: 16px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
  }

  .stat-info {
    flex: 1;
  }

  .stat-label {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .stat-value {
    font-size: 16px;
    font-weight: bold;
    color: ${COLORS.textPrimary};
  }

  .system-panels {
     width: 200px;
  }

  .connection-status {
    position: absolute;
    bottom: 20px;
    right: 20px;
    pointer-events: auto;
  }

  @media (max-width: 1024px) {
    .player-info-panel {
      width: 250px;
    }

    .system-panels {
      width: 180px;
    }
  }
`;
